import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {EAutocompleteOption} from 'webapp-lib/Components';
import {useDebouncedValue} from 'webapp-lib/utils';

import {EFTagsAutocomplete} from '#/Autocomplete/ETagsAutocomplete';
import {AutocompleteProps} from '#/Autocomplete/types';
import casusBackend, {MinimalClient} from '@lexian/api/casus-api';
import {getContactTypeIcon} from '_/utilities/contacts.utils';
import {EFAutocomplete, useWatch} from '_/utilities/form';
import {
  handlePaginatedLoadableFetch,
  PaginatedItemLoader,
} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PageItem,
  PaginatedLoadable,
  PaginationArguments,
  PaginationDirection,
  PaginationMode,
} from '_/utilities/types';

type ClientTypes = NonNullable<
  Parameters<
    typeof casusBackend.searchClients
  >[0]['parameters']['clientTypeFilter']
>;

const buildSearchClients =
  (clientTypeFilter?: ClientTypes) =>
  (
    paginationArguments: Omit<PaginationArguments, 'cursor'>,
    currentLoadable: PaginatedLoadable<PageItem<MinimalClient>[]>,
    {searchQuery}: {searchQuery: string}
  ): Promise<PaginatedLoadable<PageItem<MinimalClient>[]>> =>
    handlePaginatedLoadableFetch(
      {
        endpointFunction: casusBackend.searchClients,
        otherParams: {
          query: searchQuery,
          clientTypeFilter,
        },
        getPageItemFromResponse: (body) => body.clients,
      },
      paginationArguments,
      currentLoadable
    );

type ClientAutoCompleteProps = AutocompleteProps & {
  clientType?: ClientTypes;
  onClientsChange?: (
    clients: MinimalClient | MinimalClient[] | undefined
  ) => void;
};

const UClientAutocomplete = ({
  clientType,
  multiple = false,
  onClientsChange,
  ...otherProps
}: ClientAutoCompleteProps): React.ReactElement => {
  const [debounceSearchQuery, searchQuery, setSearchQuery] =
    useDebouncedValue('');
  const [clientListStore] = useState(
    new PaginatedItemLoader(buildSearchClients(clientType))
  );

  const selectedClients = useRef<MinimalClient[]>([]);

  const selectedClientId = useWatch({
    control: otherProps.control,
    name: otherProps.name,
    defaultValue: otherProps.defaultValue,
  });

  const clientsOptions = useMemo(
    () =>
      (clientListStore.loadable.data ?? []).map(
        ({item}): EAutocompleteOption => ({
          displayText: item.displayName,
          value: item.id,
          Icon:
            clientType === undefined
              ? getContactTypeIcon(item.contactType)
              : undefined,
        })
      ),
    [clientListStore.loadable.data, clientType]
  );

  const onLoadMoreClientsBefore = useCallback(async () => {
    await clientListStore.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Backward},
      {searchQuery}
    );
  }, [clientListStore, searchQuery]);

  const onLoadMoreClientsAfter = useCallback(async () => {
    await clientListStore.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
      {searchQuery}
    );
  }, [clientListStore, searchQuery]);

  useEffect(() => {
    (async () => {
      if (selectedClientId && !debounceSearchQuery && !multiple) {
        await clientListStore.fetch(
          {
            mode: PaginationMode.Navigate,
            key: [selectedClientId],
          },
          {searchQuery: ''}
        );
      } else {
        await clientListStore.fetch(defaultPaginationArguments, {
          searchQuery: debounceSearchQuery,
        });
      }
    })();
    // Only react to changes in debounceSearchQuery, selectedClientId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchQuery, selectedClientId]);

  function handleClientChange(
    e: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ) {
    if (onClientsChange) {
      const client = (clientListStore.loadable.data ?? []).find(
        ({item}) => item.id === e.target.value
      );
      onClientsChange(client?.item);
    }
    originalEventHandler(e);
  }

  function handleClientsChange(
    e: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ) {
    if (onClientsChange) {
      const newlySelectedOption = (
        e.target.value as EAutocompleteOption[]
      ).find(
        (option) =>
          !selectedClients.current.find((client) => client.id === option.value)
      );

      const newlySelectedClient = (clientListStore.loadable.data ?? []).find(
        ({item}) => item.id === newlySelectedOption?.value
      );

      if (newlySelectedClient) {
        const clients = [...selectedClients.current, newlySelectedClient?.item];
        onClientsChange(clients);
        selectedClients.current = clients;
      }
    }
    originalEventHandler(e);
  }

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          customOnChange={handleClientsChange}
          hasMoreAfter={clientListStore.loadable.hasMoreAfter}
          hasMoreBefore={clientListStore.loadable.hasMoreBefore}
          options={clientsOptions}
          noOptionFiltering
          onLoadMoreAfter={onLoadMoreClientsAfter}
          onLoadMoreBefore={onLoadMoreClientsBefore}
          onSearchQueryChange={setSearchQuery}
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          customOnChange={handleClientChange}
          hasMoreAfter={clientListStore.loadable.hasMoreAfter}
          hasMoreBefore={clientListStore.loadable.hasMoreBefore}
          options={clientsOptions}
          noOptionFiltering
          onLoadMoreAfter={onLoadMoreClientsAfter}
          onLoadMoreBefore={onLoadMoreClientsBefore}
          onSearchQueryChange={setSearchQuery}
        />
      )}
    </>
  );
};
export const ClientAutocomplete = observer(UClientAutocomplete);
